import './RtuClusterSection.css';

import React, { useEffect, useId, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card } from 'primereact/card';
import { useTranslation } from 'react-i18next';
import { getRtuClusterDetails } from '../../services/rtuService';
import { formatDisplayDateTime } from '../../utils/dateTimeFormat';
import TruncatedValue from '../common/TruncatedValue/TruncatedValue';
import { StaticCompositeTable, type CompositeColumn } from './StaticCompositeTable';
import { resolveServedBy } from '../RtuCluster/rtuCluster';
import type { RtuClusterDetails } from '../../types/RTU';

type Translate = (key: string, options: { defaultValue: string }) => string;

const REASON_DEFAULTS: Record<string, { key: string; text: string }> = {
  REMOVED_FROM_VALID_LIST: { key: 'removedFromList', text: 'No longer in the reported well list' },
  TYPE_CHANGED: { key: 'typeChanged', text: 'Well list type changed' },
  PARENT_TYPE_CHANGED: { key: 'parentTypeChanged', text: 'Cluster configuration changed' },
  CHILD_PRIMARY_CHANGED: { key: 'childWellChanged', text: 'Child RTU well changed' },
  // Deliberately generic: the reason never hints at other records.
  CHILD_AMBIGUOUS: { key: 'linkUnconfirmed', text: 'Link could not be confirmed' },
};

/** Close reason in plain words; an unknown code is shown as sentence-case words, never as a raw key. */
export const describeCloseReason = (reason: string | null | undefined, t: Translate): string => {
  const code = (reason ?? '').trim();
  if (!code) {
    return '';
  }
  const known = REASON_DEFAULTS[code.toUpperCase()];
  if (known) {
    return t(`rtuCluster.details.reason.${known.key}`, { defaultValue: known.text });
  }
  const words = code.replace(/[_-]+/g, ' ').toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
};

const dateRange = (fromMs: number | null | undefined, toMs: number | null | undefined, t: Translate): string => {
  const from = fromMs === null || fromMs === undefined ? '—' : formatDisplayDateTime(fromMs);
  const to =
    toMs === null || toMs === undefined
      ? t('rtuCluster.details.present', { defaultValue: 'present' })
      : formatDisplayDateTime(toMs);
  // an interval that opened and closed at the same report time is shown as that moment, not as a duration
  return fromMs !== null && fromMs !== undefined && fromMs === toMs ? from : `${from} - ${to}`;
};

/** Newest first: by the most recent moment of the entry (close time, else open time). */
const newestFirst = <T,>(items: T[], moment: (item: T) => number | null | undefined): T[] =>
  [...items].sort((left, right) => (moment(right) ?? Number.NEGATIVE_INFINITY) - (moment(left) ?? Number.NEGATIVE_INFINITY));

type LoadState = 'loading' | 'ready' | 'unavailable';

interface RtuClusterSectionProps {
  rtuId: number | string | null | undefined;
}

/**
 * "Wells and cluster" in the Detailed RTU view (Connectivity and Communications tab). Shows exactly what the cluster
 * endpoint returned for the signed-in viewer: nothing is added, inferred or filled in. It requests its data when it is
 * mounted and ignores a response that arrives after the RTU changed.
 */
const RtuClusterSection: React.FC<RtuClusterSectionProps> = ({ rtuId }) => {
  const { t } = useTranslation();
  const tx = t as unknown as Translate;
  // The response is stored with the RTU it was requested for, so a render for a new RTU never shows the previous one.
  const [result, setResult] = useState<{ key: string; state: LoadState; details: RtuClusterDetails | null }>({
    key: '',
    state: 'loading',
    details: null,
  });
  const [historyOpen, setHistoryOpen] = useState(false);
  const baseId = useId();
  const historyId = `${baseId}-history`;
  const requestKey = rtuId === null || rtuId === undefined ? '' : String(rtuId).trim();

  useEffect(() => {
    setHistoryOpen(false);
    if (requestKey === '') {
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const response = await getRtuClusterDetails(rtuId as number | string);
        if (!cancelled) {
          setResult({ key: requestKey, state: response?.recordState ? 'ready' : 'unavailable', details: response ?? null });
        }
      } catch {
        // A failed call must not break the details page; the section says so once, without logging or retrying.
        if (!cancelled) {
          setResult({ key: requestKey, state: 'unavailable', details: null });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [rtuId, requestKey]);

  const loadState: LoadState = requestKey === '' ? 'unavailable' : result.key === requestKey ? result.state : 'loading';
  const details = result.key === requestKey ? result.details : null;

  const childById = useMemo(() => {
    const map = new Map<number, { id: number; name: string }>();
    (details?.children ?? []).forEach((child) => map.set(child.rtuId, { id: child.rtuId, name: child.name }));
    return map;
  }, [details]);

  const heading = t('rtuCluster.details.title', { defaultValue: 'Wells and cluster' });
  const note = (text: string) => <p className="rtu-cluster-details__note">{text}</p>;
  const wrap = (body: React.ReactNode) => (
    <section className="rtu-cluster-details" aria-label={heading}>
      <Card title={heading} className="info-card compact-card flat-card">
        {body}
      </Card>
    </section>
  );

  if (loadState === 'loading') {
    return wrap(note(t('rtuCluster.details.loading', { defaultValue: 'Loading...' })));
  }

  if (loadState === 'unavailable' || !details) {
    return wrap(note(t('rtuCluster.details.unavailable', { defaultValue: 'Wells and cluster information is unavailable.' })));
  }

  if (details.recordState === 'FORMER') {
    return wrap(note(t('rtuCluster.details.former', { defaultValue: "This record is no longer this RTU's current well." })));
  }

  if (details.recordState === 'UNRESOLVED') {
    return wrap(note(t('rtuCluster.details.unresolved', { defaultValue: 'RTU identity needs review.' })));
  }

  const servedByText = (well: { servedBy?: string | null; childRtuId?: number | null }) => {
    const served = resolveServedBy(
      { servedBy: well.servedBy as 'THIS_RTU' | 'CHILD_RTU' | 'UNKNOWN' | undefined, childRtuId: well.childRtuId },
      (id) => childById.get(id)
    );
    switch (served.kind) {
      case 'this':
        return t('rtuCluster.servedByThis', { defaultValue: 'This RTU' });
      case 'child':
        return served.name;
      case 'childAnonymous':
        return t('rtuCluster.servedByChild', { defaultValue: 'Child RTU' });
      default:
        return t('rtuCluster.servedByUnknown', { defaultValue: 'Not reported' });
    }
  };

  const { parent, children, currentWells, formerWells, wellHistory, clusterHistory } = details;
  const formerSorted = newestFirst(formerWells, (item) => item.toUtcMs ?? item.fromUtcMs);
  const wellHistorySorted = newestFirst(wellHistory, (item) => item.closedUtcMs ?? item.openedUtcMs);
  const clusterSorted = newestFirst(clusterHistory, (item) => item.closedUtcMs ?? item.openedUtcMs);
  const historyCount = formerSorted.length + wellHistorySorted.length + clusterSorted.length;

  const wellNameLabel = t('rtuCluster.wellName', { defaultValue: 'Well name' });
  const wellIdLabel = t('rtuCluster.wellId', { defaultValue: 'Well ID' });
  const servedByLabel = t('rtuCluster.servedBy', { defaultValue: 'Served by' });
  const periodLabel = t('rtuCluster.details.period', { defaultValue: 'Period' });
  const reasonLabel = t('rtuCluster.details.reasonLabel', { defaultValue: 'Reason' });
  const masterLabel = t('rtuCluster.details.masterRtu', { defaultValue: 'Master RTU' });
  const childrenLabel = t('rtuCluster.details.childRtus', { defaultValue: 'Child RTUs' });

  const rtuLink = (id: number, name: string) => (
    <Link
      className="rtu-cluster-details__open"
      to={`/rtupage/${id}`}
      aria-label={t('rtuCluster.details.openRtu', { name, defaultValue: 'Open {{name}}' })}
      title={t('rtuCluster.details.openRtu', { name, defaultValue: 'Open {{name}}' })}
    >
      <i className="pi pi-external-link" aria-hidden="true" />
    </Link>
  );

  const historyTable = (label: string, headers: string[], rows: React.ReactNode[][]) => (
    <>
      <h3 className="rtu-cluster-details__subtitle">{label}</h3>
      <div className="rtu-cluster-details__scroll">
        <StaticCompositeTable
          rows={rows.map((cells) => Object.fromEntries(cells.map((cell, index) => [`column-${index}`, cell])))}
          columns={headers.map((header, index) => ({ field: `column-${index}`, header }))}
          ariaLabel={label}
          className="rtu-cluster-details__table rtu-cluster-details__table--history"
        />
      </div>
    </>
  );

  return wrap(
    <>
      {currentWells.length > 0 ? (
        <div className="rtu-cluster-details__scroll">
          <StaticCompositeTable
            rows={currentWells}
            columns={[
              {
                field: 'name',
                header: wellNameLabel,
                width: '33.333%',
                body: (well) => <TruncatedValue value={well.name ?? ''} label={wellNameLabel} />,
              },
              {
                field: 'wellId',
                header: wellIdLabel,
                width: '33.333%',
                body: (well) => <TruncatedValue value={well.wellId} label={wellIdLabel} />,
              },
              {
                field: 'servedBy',
                header: servedByLabel,
                width: '33.333%',
                body: (well) => <TruncatedValue value={servedByText(well)} label={servedByLabel} />,
              },
            ] as CompositeColumn[]}
            ariaLabel={t('rtuCluster.details.currentWells', { defaultValue: 'Current wells' })}
            className="rtu-cluster-details__table rtu-cluster-details__table--wells"
          />
        </div>
      ) : (
        note(t('rtuCluster.details.noWells', { defaultValue: 'No wells reported.' }))
      )}

      {(parent || children.length > 0) && (
        <table className="kv-table rtu-cluster-details__kv">
          <tbody>
            {parent && (
              <tr>
                <td>{masterLabel}</td>
                <td>
                  <span className="rtu-cluster-details__rel-item">
                    <TruncatedValue value={parent.name} label={masterLabel} />
                    {rtuLink(parent.rtuId, parent.name)}
                  </span>
                </td>
              </tr>
            )}
            {children.length > 0 && (
              <tr>
                <td>{childrenLabel}</td>
                <td>
                  {children.map((child, index) => (
                    <React.Fragment key={child.rtuId}>
                      {index > 0 ? ', ' : null}
                      <span className="rtu-cluster-details__rel-item">
                        <TruncatedValue value={child.name} label={childrenLabel} />
                        {rtuLink(child.rtuId, child.name)}
                        {child.wellId ? <span className="rtu-cluster-details__muted">{` (${child.wellId})`}</span> : null}
                      </span>
                    </React.Fragment>
                  ))}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      )}

      {historyCount > 0 && (
        <div className="rtu-cluster-details__history">
          <button
            type="button"
            className="rtu-cluster-details__history-toggle"
            aria-expanded={historyOpen}
            aria-controls={historyId}
            onClick={() => setHistoryOpen((open) => !open)}
          >
            <i className={`pi ${historyOpen ? 'pi-chevron-down' : 'pi-chevron-right'}`} aria-hidden="true" />
            <span>
              {historyOpen
                ? t('rtuCluster.details.hideHistory', { defaultValue: 'Hide history' })
                : t('rtuCluster.details.showHistory', { count: historyCount, defaultValue: 'Show history ({{count}})' })}
            </span>
          </button>
          {historyOpen && (
            <div id={historyId} className="rtu-cluster-details__history-body">
              {details.truncated && (
                <p className="rtu-cluster-details__muted">
                  {t('rtuCluster.details.olderNotShown', { defaultValue: 'Older entries are not shown.' })}
                </p>
              )}

              {formerSorted.length > 0 &&
                historyTable(
                  t('rtuCluster.details.formerWells', { defaultValue: 'Former wells' }),
                  [wellNameLabel, wellIdLabel, periodLabel],
                  formerSorted.map((well) => [well.name ?? '', well.wellId, dateRange(well.fromUtcMs, well.toUtcMs, tx)])
                )}

              {wellHistorySorted.length > 0 &&
                historyTable(
                  t('rtuCluster.details.wellAssignments', { defaultValue: 'Well assignments' }),
                  [wellIdLabel, servedByLabel, periodLabel, reasonLabel],
                  wellHistorySorted.map((entry) => [
                    entry.wellId,
                    servedByText({ servedBy: entry.servedBy }),
                    dateRange(entry.openedUtcMs, entry.closedUtcMs, tx),
                    describeCloseReason(entry.closeReason, tx),
                  ])
                )}

              {clusterSorted.length > 0 &&
                historyTable(
                  t('rtuCluster.details.clusterHistory', { defaultValue: 'Cluster' }),
                  [
                    t('rtuCluster.details.relationship', { defaultValue: 'Relationship' }),
                    t('rtuCluster.details.rtu', { defaultValue: 'RTU' }),
                    periodLabel,
                    reasonLabel,
                  ],
                  clusterSorted.map((entry) => [
                    entry.relation === 'MASTER_OF'
                      ? t('rtuCluster.details.masterOf', { defaultValue: 'Master of' })
                      : t('rtuCluster.details.childOf', { defaultValue: 'Child of' }),
                    entry.name || (entry.rtuId !== null && entry.rtuId !== undefined ? String(entry.rtuId) : ''),
                    dateRange(entry.openedUtcMs, entry.closedUtcMs, tx),
                    describeCloseReason(entry.closeReason, tx),
                  ])
                )}
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default RtuClusterSection;
